import { useEffect, useState } from 'react';

export default function HomePage() {
  const [isVisible, setIsVisible] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeService, setActiveService] = useState(0);

  useEffect(() => {
    setIsVisible(true);
    
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const services = [
    {
      title: "Brand Positioning",
      description: "Transform your personal brand into a magnetic force that attracts premium opportunities effortlessly.",
      icon: "ri-sparkling-line"
    },
    {
      title: "Revenue Systems",
      description: "Build automated income streams that scale without burning you out or compromising your creative vision.",
      icon: "ri-money-dollar-circle-line"
    },
    {
      title: "Strategic Partnerships",
      description: "Connect with brands and collaborators who align with your values and amplify your impact.",
      icon: "ri-team-line"
    },
    {
      title: "Sustainable Growth",
      description: "Grow your audience and influence authentically, without the exhausting content treadmill.",
      icon: "ri-line-chart-line"
    }
  ];

  const testimonials = [
    {
      quote: "Velour helped me 3x my income while working half the hours. I finally have the creative freedom I always wanted.",
      author: "Sarah Chen",
      role: "Content Creator & Author"
    },
    {
      quote: "They understood my vision immediately and built systems that actually work. No fluff, just results.",
      author: "Marcus Rodriguez",
      role: "Founder & Podcaster"
    },
    {
      quote: "This isn't just strategy—it's a complete shift in how I approach my business. Game-changing.",
      author: "Aisha Patel",
      role: "Designer & Educator"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5F3EE] via-[#EEF2E6] to-[#F5F3EE]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-[#F5F3EE]/95 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
        <div className="flex justify-between items-center px-8 lg:px-16 py-6">
          <h1>
            <button 
              onClick={() => scrollToSection('home')}
              className="flex items-center gap-3 cursor-pointer group"
            >
              <span 
                className="text-3xl font-light text-[#374151] tracking-wide hover:text-[#6B7280] transition-colors duration-300"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                VELOUR
              </span>
            </button>
          </h1>
          <div className="flex items-center gap-8">
            <button
              onClick={() => scrollToSection('services')}
              className="hidden md:block text-[#374151] text-base cursor-pointer hover:text-[#6B7280] transition-colors duration-300"
              style={{ fontFamily: 'Lora, serif' }}
            >
              Services
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section 
        id="home"
        className="relative flex items-center justify-center pt-48 pb-20 px-6"
      >
        {/* Hero Content */}
        <div className={`relative z-10 text-center max-w-4xl mx-auto transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="mb-6 inline-block">
            <span className="px-5 py-2 bg-[#6B7280]/10 text-[#374151] text-sm font-medium tracking-widest uppercase rounded-full border border-[#6B7280]/20" style={{ fontFamily: 'Lora, serif' }}>
              For High-Signal Creators
            </span>
          </div>
          
          <h2 
            className="text-5xl md:text-7xl lg:text-8xl leading-[1.1] font-light text-[#374151] mb-4 tracking-tight"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Turn Your Influence Into
            <span className="block text-[#6B7280] mt-2">Effortless Income</span>
          </h2>
          
          <p 
            className="text-xl md:text-2xl leading-relaxed text-[#374151]/70 max-w-3xl mx-auto mb-5"
            style={{ fontFamily: 'Lora, serif' }}
          >
            Stop trading time for money. Build a creator business that scales beautifully—without the burnout, noise, or endless hustle.
          </p>

          <button
            onClick={() => scrollToSection('contact')}
            className="px-8 py-3 bg-transparent text-[#374151] font-medium text-base tracking-wide border border-[#374151] hover:bg-[#374151] hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap rounded-lg"
            style={{ fontFamily: 'Lora, serif' }}
          >
            Start a conversation
          </button>
        </div>
      </section>

      {/* Problem Section */}
      <section className="pt-32 pb-16 px-6 lg:px-16 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h3 
              className="text-4xl md:text-5xl font-light text-[#374151] mb-1"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              You're Doing Everything Right.
              <span className="block text-[#6B7280] mt-1">So Why Does It Feel So Hard?</span>
            </h3>
          </div>

          <div className="grid md:grid-cols-3 gap-6 py-8">
            <div className="p-5 bg-gradient-to-br from-[#F5F3EE] to-[#EEF2E6] rounded-2xl hover:shadow-xl transition-all duration-300">
              <div className="w-12 h-12 flex items-center justify-center bg-[#6B7280]/10 rounded-full mb-4">
                <i className="ri-time-line text-[#6B7280] text-2xl"></i>
              </div>
              <h4 className="text-xl font-semibold text-[#374151] mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Constant Content Treadmill
              </h4>
              <p className="text-base text-[#374151]/80 leading-relaxed" style={{ fontFamily: 'Lora, serif' }}>
                You're creating non-stop but your income isn't growing. The algorithm demands more, but you're already maxed out.
              </p>
            </div>

            <div className="p-5 bg-gradient-to-br from-[#F5F3EE] to-[#EEF2E6] rounded-2xl hover:shadow-xl transition-all duration-300">
              <div className="w-12 h-12 flex items-center justify-center bg-[#6B7280]/10 rounded-full mb-4">
                <i className="ri-exchange-dollar-line text-[#6B7280] text-2xl"></i>
              </div>
              <h4 className="text-xl font-semibold text-[#374151] mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Trading Time for Money
              </h4>
              <p className="text-base text-[#374151]/80 leading-relaxed" style={{ fontFamily: 'Lora, serif' }}>
                Every dollar requires your direct involvement. You can't scale without sacrificing your life or creative vision.
              </p>
            </div>

            <div className="p-5 bg-gradient-to-br from-[#F5F3EE] to-[#EEF2E6] rounded-2xl hover:shadow-xl transition-all duration-300">
              <div className="w-12 h-12 flex items-center justify-center bg-[#6B7280]/10 rounded-full mb-4">
                <i className="ri-question-line text-[#6B7280] text-2xl"></i>
              </div>
              <h4 className="text-xl font-semibold text-[#374151] mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                No Clear Path Forward
              </h4>
              <p className="text-base text-[#374151]/80 leading-relaxed" style={{ fontFamily: 'Lora, serif' }}>
                You know you should be earning more, but every "guru" offers conflicting advice. What actually works?
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section 
        id="services"
        className="pt-32 pb-16 px-6 lg:px-16 bg-gradient-to-br from-[#374151] to-[#4B5563]"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h3 
              className="text-4xl md:text-5xl font-light text-white mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              How We Help You Win
            </h3>
            <p className="text-xl text-white/80 max-w-3xl mx-auto" style={{ fontFamily: 'Lora, serif' }}>
              Strategic systems that turn your expertise into sustainable, scalable income—without the overwhelm.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                onClick={() => setActiveService(index)}
                className={`p-6 rounded-2xl cursor-pointer transition-all duration-300 ${
                  activeService === index 
                    ? 'bg-[#6B7280] shadow-2xl scale-105' 
                    : 'bg-white/5 backdrop-blur-sm hover:bg-white/10'
                }`}
              >
                <div className="w-14 h-14 flex items-center justify-center bg-white/10 rounded-full mb-4">
                  <i className={`${service.icon} text-white text-3xl`}></i>
                </div>
                <h4 
                  className="text-2xl font-semibold text-white mb-3"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                >
                  {service.title}
                </h4>
                <p 
                  className="text-base text-white/90 leading-relaxed"
                  style={{ fontFamily: 'Lora, serif' }}
                >
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 px-6 lg:px-16 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h3 
              className="text-4xl md:text-5xl font-light text-[#374151] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Real Creators. Real Results.
            </h3>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="p-6 bg-gradient-to-br from-[#F5F3EE] to-[#EEF2E6] rounded-2xl hover:shadow-xl transition-all duration-300"
              >
                <div className="mb-4">
                  <i className="ri-double-quotes-l text-[#6B7280] text-4xl"></i>
                </div>
                <p 
                  className="text-lg text-[#374151] leading-relaxed mb-4"
                  style={{ fontFamily: 'Lora, serif' }}
                >
                  {testimonial.quote}
                </p>
                <div className="border-t border-[#6B7280]/20 pt-4">
                  <p className="font-semibold text-[#374151]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {testimonial.author}
                  </p>
                  <p className="text-sm text-[#374151]/60" style={{ fontFamily: 'Lora, serif' }}>
                    {testimonial.role}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 lg:px-16 bg-gradient-to-br from-[#6B7280] to-[#9CA3AF] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h3 
            className="text-4xl md:text-6xl font-light text-white mb-4"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Ready to Build Your Creator Empire?
          </h3>
          <p 
            className="text-xl text-white/90 mb-8 max-w-2xl mx-auto"
            style={{ fontFamily: 'Lora, serif' }}
          >
            Join the creators who've transformed their passion into sustainable, scalable income—without sacrificing their sanity.
          </p>
          <button
            onClick={() => scrollToSection('contact')}
            className="px-12 py-5 bg-white text-[#6B7280] font-bold text-base tracking-wide uppercase hover:bg-[#F5F3EE] hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap rounded-lg shadow-2xl"
            style={{ fontFamily: 'Lora, serif' }}
          >
            Let's Talk Strategy
          </button>
        </div>
      </section>

      {/* Contact Section */}
      <section 
        id="contact"
        className="py-16 px-6 lg:px-16 bg-white"
      >
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h3 
              className="text-4xl md:text-5xl font-light text-[#374151] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Start Your Transformation
            </h3>
            <p className="text-lg text-[#374151]/70" style={{ fontFamily: 'Lora, serif' }}>
              Tell us about your work and vision. We'll show you what's possible.
            </p>
          </div>

          <form 
            id="contact-form"
            data-readdy-form
            action="https://readdy.ai/api/form/d5u3sgj0o41p73073b5g"
            method="POST"
            className="space-y-6"
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const formData = new FormData(form);
              const data = new URLSearchParams();
              
              formData.forEach((value, key) => {
                data.append(key, value.toString());
              });

              fetch('https://readdy.ai/api/form/d5u3sgj0o41p73073b5g', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: data.toString(),
              })
                .then(response => {
                  if (response.ok) {
                    const successMsg = document.createElement('div');
                    successMsg.className = 'fixed top-8 left-1/2 transform -translate-x-1/2 bg-[#6B7280] text-white px-8 py-4 rounded-lg shadow-xl z-50';
                    successMsg.textContent = 'Thank you! We\'ll be in touch within 24 hours.';
                    document.body.appendChild(successMsg);
                    setTimeout(() => successMsg.remove(), 5000);
                    form.reset();
                  } else {
                    const errorMsg = document.createElement('div');
                    errorMsg.className = 'fixed top-8 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-8 py-4 rounded-lg shadow-xl z-50';
                    errorMsg.textContent = 'Something went wrong. Please try again.';
                    document.body.appendChild(errorMsg);
                    setTimeout(() => errorMsg.remove(), 5000);
                  }
                })
                .catch(() => {
                  const errorMsg = document.createElement('div');
                  errorMsg.className = 'fixed top-8 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-8 py-4 rounded-lg shadow-xl z-50';
                  errorMsg.textContent = 'Something went wrong. Please try again.';
                  document.body.appendChild(errorMsg);
                  setTimeout(() => errorMsg.remove(), 5000);
                });
            }}
          >
            <div className="grid md:grid-cols-2 gap-6">
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                required
                className="w-full px-6 py-4 bg-[#F5F3EE] border-2 border-transparent text-[#374151] placeholder-[#374151]/40 focus:outline-none focus:border-[#6B7280] transition-all duration-300 text-base rounded-lg"
                style={{ fontFamily: 'Lora, serif' }}
              />
              <input
                type="email"
                name="email"
                placeholder="Your Email"
                required
                className="w-full px-6 py-4 bg-[#F5F3EE] border-2 border-transparent text-[#374151] placeholder-[#374151]/40 focus:outline-none focus:border-[#6B7280] transition-all duration-300 text-base rounded-lg"
                style={{ fontFamily: 'Lora, serif' }}
              />
            </div>
            <textarea
              name="message"
              placeholder="Tell us about your work, audience, and what you're looking to achieve..."
              required
              maxLength={500}
              rows={6}
              className="w-full px-6 py-4 bg-[#F5F3EE] border-2 border-transparent text-[#374151] placeholder-[#374151]/40 focus:outline-none focus:border-[#6B7280] transition-all duration-300 resize-none text-base rounded-lg"
              style={{ fontFamily: 'Lora, serif' }}
            />
            <button
              type="submit"
              className="w-full px-12 py-5 bg-[#6B7280] text-white font-bold text-base tracking-wide uppercase hover:bg-[#9CA3AF] hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap rounded-lg shadow-xl"
              style={{ fontFamily: 'Lora, serif' }}
            >
              Send My Message
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 lg:px-16 bg-[#374151]">
        <div className="max-w-6xl mx-auto text-center">
          <p 
            className="text-base text-white/60"
            style={{ fontFamily: 'Lora, serif' }}
          >
            © 2025 Velour Strategy. All rights reserved. · <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#6B7280] transition-colors duration-300 cursor-pointer">Powered by Readdy</a>
          </p>
        </div>
      </footer>
    </div>
  );
}
